/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowABFlag } from "@packages/ab";
import { HallowAnalyticsEvent, useViewedScreen } from "@packages/analytics";
import type { ThemeType } from "@packages/ui";
import {
  cssMediaQueryMinWidthTablet,
  fontSizes,
  fontWeights,
  lineHeights,
  numericPercentages,
  spacing,
  Text,
} from "@packages/ui";

import { CtaWithABTest } from "../CTA";

const IN_SAFARI = "@supports (hanging-punctuation: first)";

const containerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "row-reverse",
  flexWrap: "wrap",
  justifyContent: "space-evenly",
  marginBottom: spacing.xxl,
  rowGap: spacing.xxl,
  transformStyle: "preserve-3d",
};

const viewportStyles: Interpolation = {
  maxWidth: "90vw",
  position: "relative",
  width: "500px",
  zIndex: 0,
  height: "50vh",
  transformStyle: "preserve-3d",
  [IN_SAFARI]: {
    height: "45vh",
    transformStyle: "flat",
  },
  [cssMediaQueryMinWidthTablet]: {
    height: "70vh",
  },
};

const imageStyles: Interpolation = {
  backfaceVisibility: "hidden",
  left: numericPercentages[50],
  objectFit: "cover",
  objectPosition: "center",
  overflow: "hidden",
  position: "absolute",
  willChange: "transform",
  height: "45vh",
  [cssMediaQueryMinWidthTablet]: {
    height: "60vh",
  },
};

const backImageStyles: Interpolation = {
  top: 0,
  zIndex: 0,
  transform: "translateZ(0) translateX(-15%) scale(0.8)",
  [IN_SAFARI]: {
    transform: "translateX(-15%) translateY(-5%) scale(0.85)",
  },
  [cssMediaQueryMinWidthTablet]: {
    transform: "translateZ(0) scale(0.8)",
    [IN_SAFARI]: { transform: "scale(0.8)" },
  },
};

const frontImageStyles: Interpolation = {
  top: "20%",
  zIndex: 1,
  transform: "translateZ(2px) translateX(-70%) scale(0.7)",
  [IN_SAFARI]: {
    transform: "translateX(-80%) translateY(-5%) scale(0.85)",
  },
  [cssMediaQueryMinWidthTablet]: {
    transform: "translateZ(2px) translateX(-100%) scale(0.7)",
    [IN_SAFARI]: {
      transform: "translateX(-65%) translateY(-20px) scale(0.85)",
    },
  },
};

const heroTextStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  gap: "none",
  justifyContent: "flex-start",
  alignItems: "center",
  textAlign: "center",
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "flex-start",
    textAlign: "start",
  },
};

const titleStyles: Interpolation = {
  marginBottom: spacing.s,
  fontSize: fontSizes.headlineL,
  fontWeight: fontWeights.headlineL,
  lineHeight: lineHeights.headlineL,
  [cssMediaQueryMinWidthTablet]: {
    fontSize: fontSizes.displayL,
    fontWeight: fontWeights.displayL,
    lineHeight: lineHeights.displayL,
  },
};

const subtitleStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsHigher,
  marginBottom: spacing.l,
  fontSize: fontSizes.bodyL,
  fontWeight: fontWeights.bodyL,
  lineHeight: lineHeights.bodyL,
  [cssMediaQueryMinWidthTablet]: {
    fontSize: fontSizes.bodyXL,
    fontWeight: fontWeights.bodyXL,
    lineHeight: lineHeights.bodyXL,
  },
});

type HeroProps = {
  title: string;
  subtitle: string;
  url: string;
  buttonText: string;
  backImageUrl: string;
  frontImageUrl: string;
};

export const Hero = ({
  title,
  subtitle,
  url,
  backImageUrl,
  frontImageUrl,
  buttonText,
}: HeroProps) => {
  useViewedScreen({ screen: "homepage" });
  return (
    <>
      <style>{"#scrollBody { perspective: 10px; }"}</style>
      <div key={"images"} css={containerStyles}>
        <div css={viewportStyles}>
          <img
            key={"backImage"}
            src={backImageUrl}
            alt={""}
            css={[imageStyles, backImageStyles]}
          />
          <img
            key={"frontImage"}
            src={frontImageUrl}
            alt={""}
            css={[imageStyles, frontImageStyles]}
          />
        </div>
        <div key={"texts"} css={heroTextStyles}>
          <Text key={"title"} css={titleStyles} asChild>
            <h1 dangerouslySetInnerHTML={{ __html: title }} />
          </Text>
          <Text key={"subtitle"} as={"p"} css={subtitleStyles}>
            {subtitle}
          </Text>
          <CtaWithABTest
            baseProps={{
              href: url,
              event: HallowAnalyticsEvent.TappedTryHallowForFree,
              eventProperties: {
                screen_name: "homepage",
                button: buttonText,
                button_location: "hero_section",
              },
              variant: "primary",
              size: "l",
              children: buttonText,
            }}
            controlProps={{ href: "https://hallow.app.link/PPX0CLYx9Vb" }}
            treatmentProps={{
              href: "https://hallow-web.app.link/e/5GesCoL7kWb",
            }}
            flag={HallowABFlag.WebHomepageCTADestination}
          />
        </div>
      </div>
    </>
  );
};
